import RefreshIcon from '@mui/icons-material/Refresh'
import { Card, CardContent, IconButton, styled } from '@mui/material'
import React from 'react'

import TabHeader from '@/app/component/atom/tab-header'
import TablePagination from '@/app/component/atom/table-pagination'
import CustomTooltip from '@/app/component/atom/tooltip'
import ExportList from '@/app/module/exports/component/export-list'
import { useDeleteExport } from '@/app/module/exports/hooks/use-delete-export'
import { useDownloadExport } from '@/app/module/exports/hooks/use-download-export'
import { useGetCxUploads } from '@/app/module/user/hooks/use-get-cx-uploads'

type Query = {
  page: number
  size: number
}

const CxUploads: React.FC = () => {
  const [query, setQuery] = React.useState<Query>({
    page: 1,
    size: 10,
  })

  const { data, error, isFetching, isLoading, refetch } = useGetCxUploads(query)
  const [deleteExportFn] = useDeleteExport()
  const [downloadExportFn, { downloading }] = useDownloadExport()

  const handleDelete = React.useCallback((exportId: number) => deleteExportFn(exportId), [deleteExportFn])

  const handleFileDownload = React.useCallback(
    (exportId: number, filename: string) => downloadExportFn(exportId, filename),
    [downloadExportFn],
  )

  const exports = data?.exports || []
  const loading = downloading || isFetching || isLoading

  return (
    <Container>
      <TabHeader
        title="Customer Support Uploads"
        action={
          <Actions>
            <CustomTooltip title="Reload files">
              <IconButton disabled={loading} onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </CustomTooltip>
          </Actions>
        }
      />
      <ContentContainer>
        <ExportList
          data={exports}
          error={error}
          loading={loading}
          onDelete={handleDelete}
          onFileDownload={handleFileDownload}
        />
        {!!data?.total && (
          <TablePagination
            namespace="uploads"
            page={query.page}
            pageSizeValues={pageSizeValues}
            size={query.size}
            total={data?.total || 0}
            onPageChange={(page) => setQuery((s) => ({ ...s, page }))}
            onPageSizeChange={(size) => setQuery((s) => ({ ...s, size }))}
          />
        )}
      </ContentContainer>
    </Container>
  )
}

const Container = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const ContentContainer = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
})

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}))

const pageSizeValues = [10, 20, 50, 100].map((size) => ({
  label: size.toString(),
  value: size,
}))

export default CxUploads
